// The bottom contextual toolbar for the mobile touch editor — the thumb-zone replacement for the
// desktop FloatingToolbar. A horizontally-scrolling row of big (44px+) touch targets that swaps
// between global actions (nothing selected) and per-layer actions (a layer selected). All chrome
// is T3 dark since it floats below the canvas, not over the white post card.

import { useState } from 'react'
import {
  ArrowDownToLine,
  ArrowUpToLine,
  Check,
  Copy,
  ImageIcon,
  Layers,
  Palette,
  Pencil,
  Plus,
  Shapes,
  SlidersHorizontal,
  Trash2,
  Type,
} from 'lucide-react'
import type { CompositionEditor } from './use-composition-editor'
import { useComposerEditor } from './CompositionEditorProvider'
import { useConfirmableDelete } from './confirm-delete'
import { ColorPickerSwatch } from './ColorPickerSwatch'
import { ReplacePhotoPopover } from './ReplacePhotoPopover'
import { activeRangeFor, useTextRangeSelection } from './use-text-range-selection'

// Shared touch-target style for the toolbar buttons. Extracted so the "Replace photo"
// popover trigger (which can't wrap TBtn) matches the rest of the row.
export const TBTN_CLASS =
  'flex h-14 min-w-[60px] flex-shrink-0 flex-col items-center justify-center gap-1 rounded-xl px-2 text-[11px] font-medium text-paper-dim transition-colors active:bg-bg-3'

function palette(editor: CompositionEditor): string[] {
  const p = editor.comp.brand.palette
  return [p.primary, p.secondary, p.accent, p.neutralLight, p.neutralDark, '#FFFFFF', '#000000']
    .filter((c): c is string => !!c)
    .filter((v, i, a) => a.indexOf(v) === i)
}

export function TBtn({
  icon,
  label,
  onClick,
  active,
  danger,
  keepFocus,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
  active?: boolean
  danger?: boolean
  /** Keep the inline text editor's focus + selection on tap (so a style click restyles the
   *  selected span instead of blurring the editor → falling back to whole-layer). */
  keepFocus?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={keepFocus ? e => e.preventDefault() : undefined}
      className={`flex h-14 min-w-[60px] flex-shrink-0 flex-col items-center justify-center gap-1 rounded-xl px-2 text-[11px] font-medium transition-colors ${
        active
          ? 'text-ink-on-acid bg-acid'
          : danger
            ? 'text-red-300 active:bg-bg-3'
            : 'text-paper-dim active:bg-bg-3'
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  )
}

export function MobileLayerToolbar({
  leading,
}: {
  /** Extra buttons rendered FIRST in the selected-layer row (e.g. the video editor's
   *  clip Timing button) — lets callers extend the row without forking the toolbar. */
  leading?: React.ReactNode
} = {}) {
  const ctx = useComposerEditor()
  const [colorOpen, setColorOpen] = useState(false)
  const { confirmingId, requestDelete } = useConfirmableDelete(ctx?.editor as CompositionEditor)
  const layerId = ctx?.editor.selected?.id ?? ''
  // When a span is selected in the inline editor, text styling targets just that span (parity with
  // desktop). Falls back to whole-layer when no span is selected — so on a device where the tap
  // can't keep the selection, behavior is unchanged (no regression).
  const range = useTextRangeSelection(s => activeRangeFor(s.range, layerId))
  if (!ctx) return null
  const { editor, dock } = ctx
  const layer = editor.selected
  const colors = palette(editor)

  const currentColor =
    layer?.type === 'text'
      ? (layer.runs[0]?.color ?? layer.color)
      : layer?.type === 'shape' && layer.fill.kind === 'solid'
        ? layer.fill.color
        : layer?.type === 'decor'
          ? layer.color
          : '#FFFFFF'

  const applyColor = (c: string) => {
    if (!layer) return
    if (layer.type === 'text') {
      if (range) editor.restyleTextRange(layer.id, range.start, range.end, { color: c })
      else editor.setTextColor(layer.id, c)
    } else if (layer.type === 'shape') editor.setLayerFill(layer.id, { kind: 'solid', color: c })
    else if (layer.type === 'decor')
      editor.updateLayer(layer.id, { color: c } as Partial<typeof layer>)
  }
  const scaleText = (factor: number) => {
    if (!layer) return
    if (range) editor.scaleTextRange(layer.id, range.start, range.end, factor)
    else editor.scaleTextSize(layer.id, factor)
  }
  const canColor = layer?.type === 'text' || layer?.type === 'shape' || layer?.type === 'decor'

  return (
    <div className="relative border-t border-line bg-bg-2 pb-[env(safe-area-inset-bottom)]">
      {/* Color popover */}
      {colorOpen && layer && canColor && (
        <div className="absolute inset-x-0 bottom-full flex items-center gap-2 overflow-x-auto border-t border-line bg-bg-2 px-3 py-2">
          {colors.map(c => (
            <button
              key={c}
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => applyColor(c)}
              className={`h-7 w-7 flex-shrink-0 rounded-full border ${
                currentColor.toLowerCase() === c.toLowerCase()
                  ? 'border-acid ring-1 ring-acid'
                  : 'border-black/20'
              }`}
              style={{ background: c }}
            />
          ))}
          <ColorPickerSwatch value={currentColor} onPick={applyColor} size={28} />
        </div>
      )}

      <div className="flex items-center gap-1 overflow-x-auto px-2 py-1.5">
        {!layer ? (
          <>
            <TBtn icon={<Type size={18} />} label="Text" onClick={() => editor.addText()} />
            <TBtn icon={<Shapes size={18} />} label="Shape" onClick={() => editor.addShape()} />
            <ReplacePhotoPopover
              title="Add photo"
              onPick={attachmentId => editor.addImage(attachmentId)}
            >
              <button type="button" className={TBTN_CLASS}>
                <ImageIcon size={18} />
                <span>Photo</span>
              </button>
            </ReplacePhotoPopover>
            <TBtn icon={<Layers size={18} />} label="Layers" onClick={dock.openDock} />
          </>
        ) : (
          <>
            {leading}
            {layer.type === 'text' && (
              <>
                <TBtn
                  icon={<Pencil size={18} />}
                  label="Edit"
                  onClick={() => editor.beginTextEdit(layer.id)}
                />
                <TBtn
                  icon={<Plus size={18} />}
                  label={range ? 'Bigger ·sel' : 'Bigger'}
                  keepFocus
                  onClick={() => scaleText(1.12)}
                />
                <TBtn
                  icon={<span className="text-[15px] leading-none font-semibold">A−</span>}
                  label={range ? 'Smaller ·sel' : 'Smaller'}
                  keepFocus
                  onClick={() => scaleText(1 / 1.12)}
                />
              </>
            )}
            {canColor && (
              <TBtn
                icon={<Palette size={18} />}
                label="Color"
                active={colorOpen}
                keepFocus
                onClick={() => setColorOpen(v => !v)}
              />
            )}
            {/* Image edits — Replace photo (the #1 image action) + fit toggle, mirroring the
                desktop FloatingToolbar so the image sheet can actually edit the image. */}
            {layer.type === 'image' && (
              <>
                <ReplacePhotoPopover
                  onPick={attachmentId => editor.replaceImageSrc(layer.id, attachmentId)}
                >
                  <button type="button" className={TBTN_CLASS}>
                    <ImageIcon size={18} />
                    <span>Replace</span>
                  </button>
                </ReplacePhotoPopover>
                <TBtn
                  icon={<span className="text-[15px] leading-none font-semibold">⤢</span>}
                  label={layer.objectFit === 'cover' ? 'Fill' : 'Fit'}
                  onClick={() =>
                    editor.updateLayer(layer.id, {
                      objectFit: layer.objectFit === 'cover' ? 'contain' : 'cover',
                    })
                  }
                />
              </>
            )}
            <TBtn
              icon={<Copy size={18} />}
              label="Duplicate"
              onClick={() => editor.duplicateLayer(layer.id)}
            />
            <TBtn
              icon={<ArrowUpToLine size={18} />}
              label="Front"
              onClick={() => editor.reorder(layer.id, 'front')}
            />
            <TBtn
              icon={<ArrowDownToLine size={18} />}
              label="Back"
              onClick={() => editor.reorder(layer.id, 'back')}
            />
            {/* Full inspector (fit/reposition/radius, opacity, rotation, fonts, shape kind,
                overlay fill) lives in the dock — one tap away for any selected layer. */}
            <TBtn icon={<SlidersHorizontal size={18} />} label="More" onClick={dock.openDock} />
            <TBtn
              icon={<Trash2 size={18} />}
              label={confirmingId === layer.id ? 'Sure?' : 'Delete'}
              danger
              onClick={() => requestDelete(layer)}
            />
            <TBtn icon={<Check size={18} />} label="Done" onClick={() => editor.select(null)} />
          </>
        )}
      </div>
    </div>
  )
}
